/**
 * Which counters are free for an officer on the live roster, read off the grid — the live-roster
 * counterpart of the generate wizard's counterVacanciesAt (utils/hwCounterVacancy), producing the same
 * CounterVacancy states so the same green/red picker can show them.
 */
import type { CounterVacancy } from '@/utils/hwCounterVacancy';

interface GridCell { officers: string[]; unavailable: boolean }

/**
 * The shared rule. `othersAt(slot)` lists who else is on the counter then (the asking officer
 * already left out); `blockedAt(slot)` says it is out of service.
 *   free    – nobody else there from startSlot, until the next other officer (or out of service) / end of shift
 *   taken   – someone else is there at startSlot, until they leave
 *   blocked – out of service at startSlot
 */
function vacancyFrom(
  numSlots: number,
  startSlot: number,
  othersAt: (slot: number) => string[],
  blockedAt: (slot: number) => boolean,
  nameOf: (officerKey: string) => string,
): CounterVacancy {
  const at = Math.min(Math.max(startSlot, 0), numSlots - 1);
  if (blockedAt(at)) return { state: 'blocked' };
  const first = othersAt(at)[0];
  if (first !== undefined) {
    let until = at + 1;
    while (until < numSlots && othersAt(until)[0] === first) until += 1;
    return { state: 'taken', untilSlot: until, occupant: { sn: 0, name: '', label: nameOf(first), handwritten: false } };
  }
  let until = at + 1;
  while (until < numSlots && othersAt(until).length === 0 && !blockedAt(until)) until += 1;
  return { state: 'free', untilSlot: until };
}

/** `counterNum` (1-based) from the counter grid, whose cell `(counterNum-1)*numSlots+slot` lists its officers. */
export function counterVacancyOnGrid(
  cells: GridCell[],
  numSlots: number,
  counterNum: number,
  startSlot: number,
  forOfficer: string,
  nameOf: (officerKey: string) => string,
): CounterVacancy {
  const cell = (slot: number) => cells[(counterNum - 1) * numSlots + slot];
  return vacancyFrom(
    numSlots, startSlot,
    (slot) => (cell(slot)?.officers ?? []).filter((o) => o !== forOfficer),
    (slot) => !!cell(slot)?.unavailable,
    nameOf,
  );
}

/** The same, from the officer view's data: officer key -> counter number per slot (0 = none). */
export function counterVacancyOnSchedules(
  schedules: Record<string, number[]>,
  numSlots: number,
  counterNum: number,
  startSlot: number,
  forOfficer: string,
  nameOf: (officerKey: string) => string,
): CounterVacancy {
  const keys = Object.keys(schedules).filter((k) => k !== forOfficer);
  return vacancyFrom(
    numSlots, startSlot,
    (slot) => keys.filter((k) => schedules[k]?.[slot] === counterNum),
    () => false,
    nameOf,
  );
}
